/**
 * THE COPY THAT CARRIES THE MEANING — `tasks/practice-documents.md` §0, §1.1, §7.
 *
 * These strings are the feature as far as a practice is concerned, so they live as named constants
 * rather than inline in a template: the specs assert the exact sentences, and a rewrite that
 * quietly softened one would fail rather than ship.
 *
 * They sit in their own file because Phase 8 split the tab into a list and two dialogs, and the
 * same sentences are now said in more than one of them. Two copies of "Patients download it,
 * complete it, and upload it back" is two chances for the panel and the dialog to disagree about
 * what happens to a patient — which is the one thing this screen exists to be right about.
 *
 * ── THE THREE THINGS THESE SENTENCES EXIST TO GET RIGHT ──────────────────────────────────────
 *
 * **1. `fillMode` is translated, never printed.** `embedded` and `download` are storage values; the
 * question a practice is actually asking is *what happens to the patient who gets this*. So the
 * panel says "Patients fill this in on screen" or "Patients download it, complete it, and upload it
 * back", and the enum never reaches the screen. §1.1's three tiers are an implementation detail of
 * that one sentence.
 *
 * **2. `hasAcroForm` and `hasTextLayer` are NOT user-facing.** They exist so a later phase can
 * upgrade a tier B document (§1.1 — synthesise AcroForm fields onto a flat PDF with a text layer).
 * "Has text layer: true" answers no question a practice manager has. They are used here for exactly
 * one thing: explaining *why* a document is download-only, in a sentence about the document rather
 * than about its metadata. See `fillModeReason`.
 *
 * **3. `requiredInInvite` states its consequence.** §0 decision 4 makes it a HARD BLOCK: the invite
 * cannot be finished until the document comes back (§5 — `isStepValid` returns false). §5 names the
 * foreseeable cost out loud, and so does this screen: a patient onboarding at 9pm with no printer
 * is stopped. That is the practice's call to make and nothing here argues with it — it only refuses
 * to let it be switched on by someone who does not know what it does.
 */

import {
  PracticeDocumentDto, PracticeDocumentLinkDto, PracticeDocumentSendOn, PracticeDocumentService,
} from '../../../core/services/practice-document.service';

/** §1.1 tier A. Phase 5 builds the viewer; the panel still has to say what the mode means. */
export const EMBEDDED_EXPLANATION = 'Patients fill this in on screen.';

/** §1.1 tier C — and tier B until AcroForm synthesis lands. */
export const DOWNLOAD_EXPLANATION = 'Patients download it, complete it, and upload it back.';

/**
 * §0 decision 4. The consequence, in the practice's own terms, at the switch that causes it.
 *
 * Not an argument about whether a hard block is wise — that is settled and it is theirs to make.
 * The 9pm example is in §5 because it is the realistic failure, and a practice that enables this
 * should have read it before a patient meets it.
 */
export const REQUIRED_IN_INVITE_CONSEQUENCE =
  'A patient cannot finish their invite until this document has been returned. Someone '
  + 'onboarding at 9pm with no printer will be held at this step until they can complete and '
  + 'upload it.';

/** Why the file endpoint is separate from the PUT, in the terms the practice cares about. */
export const REPLACE_FILE_NOTICE =
  'A replacement is inspected again, and that can change how patients complete it. If the new '
  + 'PDF has fillable fields where the old one did not, patients will fill it in on screen '
  + 'instead of downloading it — and if it goes the other way, patients who used to complete it '
  + 'on screen will start downloading it instead.';

/**
 * §7. THIS NOTICE USED TO SAY THE OPPOSITE, and saying the opposite is now the dangerous thing.
 *
 * Through Phase 3 it read "Nothing on this screen sends a document to a patient today", which was
 * true and worth saying: the difference between a practice that knows nothing is going out and one
 * that is waiting for returns. Phase 4 made it false — an appointment trigger raises an ask the
 * moment a matching appointment is booked, and the scheduled job sends it on the day the rule
 * names. A practice still reading the old sentence would configure a trigger believing it inert.
 *
 * The same reasoning, pointed the other way: a practice must not be surprised by a send.
 */
export const NOT_YET_DELIVERED_NOTICE =
  'These settings take effect as soon as they are saved. A document linked to an appointment type '
  + 'is asked for when a matching appointment is booked, and one set to send with an invite goes '
  + 'out with the next invite. Triggers set for a future day are sent on that day.';

/**
 * WHAT HAPPENS TO A PATIENT WHO RECEIVES THIS DOCUMENT — the one sentence the row exists for.
 *
 * ── WHY THIS IS NOT CALLED `fillModeExplanation` ANY MORE ────────────────────────────────────
 *
 * It was, and the name is what made the bug easy to write. `fillMode` is the §1.1 inspection's
 * answer to ONE question — can a patient type into this PDF on screen, or must they print it —
 * and a function named after it reads as a translation of an enum, so nothing about it invites
 * the question "does this patient fill it in at all?".
 *
 * They very often do not. `requiresResponse: false` (§14.2) is a document that is ATTACHED and
 * never asked back: no link, no return, nothing to review. For one of those, every clause of the
 * download sentence is false — the patient does not download it, is not asked to complete it, and
 * has nothing to upload — and the row said all three under the name of an unrelated flag.
 *
 * So `requiresResponse` is read FIRST. `fillMode` describes how a patient completes a document,
 * and a document nobody completes has no such description to give.
 *
 * ── THE FALLBACK, UNCHANGED ──────────────────────────────────────────────────────────────────
 *
 * An unknown `fillMode` — a mode a later phase adds that this build has never heard of — still
 * falls back to the download sentence rather than printing itself, because "download it and bring
 * it back" is the instruction that works for every document that IS asked back (§9 decision 4
 * takes the same fallback for the inspection booleans, for the same reason). A null document falls
 * back the same way rather than being called information-only, which would be a claim.
 */
export function patientOutcomeSentence(document: PracticeDocumentDto | null): string {
  if (!document) return DOWNLOAD_EXPLANATION;
  if (!requiresResponse(document)) return INFORMATION_ONLY_SENTENCE;

  return document.fillMode === 'embedded' ? EMBEDDED_EXPLANATION : DOWNLOAD_EXPLANATION;
}

/**
 * WHY a document is download-only — the only place `hasAcroForm` / `hasTextLayer` are used.
 *
 * They are read to produce a sentence about the document, never rendered as the booleans they
 * are. `null` for an embedded document: "patients fill this in on screen" needs no excuse.
 *
 * `null` for an INFORMATION-ONLY document too, and for the same reason the sentence above changes
 * for one: "this PDF has no fillable fields, so there is nothing for a patient to type into on
 * screen" is a true fact about a file and a complete irrelevance about a document no patient is
 * ever asked to type into. It explained a restriction that is not being applied to anybody.
 */
export function downloadOnlyReason(document: PracticeDocumentDto | null): string | null {
  if (!document || document.fillMode === 'embedded') return null;
  if (!requiresResponse(document)) return null;

  if (document.hasAcroForm) {
    return 'This PDF has fillable fields, but it is set to be completed away from the screen.';
  }
  if (document.hasTextLayer) {
    return 'This PDF has no fillable fields, so there is nothing for a patient to type into on '
      + 'screen.';
  }
  return 'This is a scan or a photograph rather than a text PDF, so it can only be completed on '
    + 'paper.';
}

/**
 * The material icon that goes with the sentence above. Never the enum, and never a label.
 *
 * An information-only document borrows the picker's own icon rather than inventing a second one
 * for the same fact: `attach_email` is what the attachment picker already draws beside
 * `INFORMATION_ONLY_SENTENCE`, and the two screens showing different symbols for one flag is the
 * drift this file exists to prevent.
 */
export function patientOutcomeIcon(document: PracticeDocumentDto | null): string {
  if (!document) return 'download_for_offline';
  if (!requiresResponse(document)) return responseModeIcon(document);

  return document.fillMode === 'embedded' ? 'edit_note' : 'download_for_offline';
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * §14.2 / §14.4 — `requiresResponse`, and what a whole selection will do
 *
 * These live beside the `fillMode` sentences rather than in the shared picker's own folder,
 * because they are the SAME register and are said in three places: the settings dialog that sets
 * the flag, the picker that shows it per document, and the picker's summary of the selection. The
 * settings screen promising one thing while the send screen says another is exactly the drift the
 * split into this file was made to prevent — and the picker asking for it from here is a
 * one-directional import (this file knows nothing about the picker, or about SharedModule).
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** What `requiresResponse: true` means to the patient, per document. */
export const REQUIRES_RESPONSE_SENTENCE =
  'The patient is asked to complete this and send it back.';

/** What `requiresResponse: false` means to the patient, per document. */
export const INFORMATION_ONLY_SENTENCE =
  'Information only — the patient is sent the file and is not asked to return anything.';

/**
 * §14.2 at the moment it is decided, in the shape `REQUIRED_IN_INVITE_CONSEQUENCE` set.
 *
 * ── WHERE THIS IS SAID, AND WHY IT MOVED ─────────────────────────────────────────────────────
 *
 * It used to be shown in the SETTINGS dialog, revealed when a `requiresResponse` toggle was
 * switched off. That toggle is gone. An information sheet is information-only BY DEFINITION - it
 * is what the tab's second section means - so a control offering to make one into a document that
 * is asked back was offering to make it something it is not, and what it actually did was move a
 * row between two sections of the tab behind the dialog, from a switch three fields down a form.
 *
 * The kind is now settled once, by which section's add icon was clicked, so this sentence is said
 * in the UPLOAD dialog: the one screen where the choice is still open, and therefore the only one
 * where reading it can change anything. The settings dialog states the kind read-only in its first
 * line and configures within it.
 *
 * What it lists is everything an information sheet does NOT do: no link, no return, nothing to
 * review, and no dedupe - the same sheet can go out as often as they like. The schema default is
 * still TRUE (§14.2) because a document wrongly marked as needing a response fails LOUDLY (a
 * patient is asked for something nobody wanted back), while one wrongly marked information-only
 * fails silently - the practice waits for a return that was never asked for.
 */
export const INFORMATION_ONLY_CONSEQUENCE =
  'This document is attached to the emails it is sent with and the patient is never asked to '
  + 'return it. No link is created, nothing comes back, there is nothing to review, and it can be '
  + 'sent to the same patient as often as you like.';

/** True where the row asks for something back. Absent is TRUE — see the DTO's own note. */
export function requiresResponse(document: { requiresResponse?: boolean } | null): boolean {
  return !!document && document.requiresResponse !== false;
}

/** The per-document sentence. Never the boolean, in either direction. */
export function responseModeSentence(document: { requiresResponse?: boolean } | null): string {
  return requiresResponse(document) ? REQUIRES_RESPONSE_SENTENCE : INFORMATION_ONLY_SENTENCE;
}

/** The material icon that goes with the sentence above. */
export function responseModeIcon(document: { requiresResponse?: boolean } | null): string {
  return requiresResponse(document) ? 'assignment_return' : 'attach_email';
}

/**
 * WHAT THE SELECTION AS A WHOLE WILL DO — §14.4, and the reason the picker is a component rather
 * than a checkbox list.
 *
 * The consequence of ticking a box is not a property of that box. Three information sheets go out
 * as three attachments and no link; add one document that must come back and the SAME three sheets
 * now ride an email that also carries a link. A user ticking a fourth box has to be able to see
 * that change, and no per-row sentence can tell them.
 *
 * `null` for an empty selection: there is nothing to describe, and a sentence about nothing is a
 * sentence a user learns to stop reading.
 */
export function selectionConsequence(documents: Array<{ requiresResponse?: boolean }>): string
  | null {
  const chosen = documents || [];
  if (!chosen.length) return null;

  const returning = chosen.filter((d) => requiresResponse(d)).length;
  const informationOnly = chosen.length - returning;

  if (!returning) {
    return `${plural(informationOnly, 'file is', 'files are')} attached to the email. No link is `
      + 'created, because there is nothing for the patient to return.';
  }

  if (!informationOnly) {
    return `One link is created for ${plural(returning, 'the document', 'all ' + returning
      + ' documents')} the patient has to send back.`;
  }

  return `One link is created for ${plural(returning, 'the document', returning + ' documents')} `
    + `the patient has to send back, and ${plural(informationOnly, 'the other file rides',
      'the other ' + informationOnly + ' files ride')} the same email as an attachment.`;
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

/**
 * A re-inspection that changed the tier is the one outcome worth interrupting for: the practice
 * has just changed how their patients complete this document, possibly without meaning to.
 */
export function replacedMessage(before: string, after: string): string {
  if (before === after) return 'File replaced.';
  return after === 'embedded'
    ? 'File replaced. Patients now fill this in on screen, where before they had to download it.'
    : 'File replaced. Patients now download it, complete it, and upload it back, where before '
      + 'they filled it in on screen.';
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * §3.2 / §24.2 — APPOINTMENT TRIGGERS: one unit on the wire, two units on the screen
 *
 * `offsetDays` is stored in DAYS and nothing else. §3.2 says why in one line: "Two units is how an
 * off-by-seven arrives." A practice, though, does not configure a follow-up questionnaire in
 * twenty-one days — they configure it in three weeks, and a control that made them do the
 * multiplication themselves is a control that will eventually be given 3 where 21 was meant.
 *
 * So the conversion happens HERE, at the edge, in functions that are each other's inverse:
 * `toOffsetDays` on the way in, `offsetAmountValue` / `offsetAmountLabel` on the way out.
 * Everything between them — the DTO, the service, the request body, the server, the column —
 * carries days. That is the whole of the design, and it is why these are pure functions in a copy
 * file rather than arithmetic spread through a form: the round trip is one assertable claim.
 *
 * `preferredOffsetUnit` is the third piece, and it exists so reading a link back does not silently
 * restate what a practice configured. A stored 21 must come back as "3 weeks", not "21 days" — not
 * because 21 days is wrong (it is the same instant) but because a practice that reads back
 * something they did not type stops trusting the screen.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export type PracticeDocumentOffsetUnit = 'days' | 'weeks';

/** ONE unit crosses the wire. This is the only place a week becomes a number of days. */
export function toOffsetDays(value: number | null | undefined,
  unit: PracticeDocumentOffsetUnit): number {
  const amount = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return unit === 'weeks' ? amount * 7 : amount;
}

/**
 * The unit a stored offset is most honestly read back in.
 *
 * Weeks only when the number is a whole number of them and there is one to show: 21 becomes
 * 3 weeks, 7 becomes 1 week, 10 stays 10 days, 0 stays 0 days. Zero deliberately stays in days —
 * "0 weeks" is a phrase nobody has ever meant, and an offset of 0 is "on the day" anyway.
 */
export function preferredOffsetUnit(offsetDays: number | null | undefined)
  : PracticeDocumentOffsetUnit {
  const days = typeof offsetDays === 'number' && Number.isFinite(offsetDays) ? offsetDays : 0;
  return days > 0 && days % 7 === 0 ? 'weeks' : 'days';
}

/** The stored offset, in the unit `preferredOffsetUnit` chose. The inverse of `toOffsetDays`. */
export function offsetAmountValue(offsetDays: number | null | undefined): number {
  const days = typeof offsetDays === 'number' && Number.isFinite(offsetDays) ? offsetDays : 0;
  return preferredOffsetUnit(days) === 'weeks' ? days / 7 : days;
}

/** "3 weeks", "1 week", "10 days", "1 day" — the number a practice typed, in the words they used. */
export function offsetAmountLabel(offsetDays: number | null | undefined): string {
  const value = offsetAmountValue(offsetDays);
  const unit = preferredOffsetUnit(offsetDays);
  if (unit === 'weeks') return value === 1 ? '1 week' : `${value} weeks`;
  return value === 1 ? '1 day' : `${value} days`;
}

/**
 * WHEN THIS TRIGGER SENDS, as a phrase — the second half of every chip.
 *
 * Present tense and no verb of dispatch ("3 weeks before the appointment", not "sent 3 weeks
 * before"), because this tab is configuration and says so at the top: a chip claiming a document
 * had gone out would contradict the standing notice three inches above it.
 *
 * `before` / `after` with an offset of 0 is a real configuration and gets its own phrase. Reading
 * it as "0 days before the appointment" would be arithmetic rather than English.
 */
export function sendOnPhrase(sendOn: PracticeDocumentSendOn | string,
  offsetDays: number | null | undefined): string {
  if (sendOn === 'confirmation') return 'when the appointment is booked';

  const days = typeof offsetDays === 'number' && Number.isFinite(offsetDays) ? offsetDays : 0;
  if (days === 0) return 'on the day of the appointment';

  const amount = offsetAmountLabel(days);
  if (sendOn === 'after') return `${amount} after the appointment`;
  return `${amount} before the appointment`;
}

/** The three `sendOn` values as a practice reads them, for the picker. */
export const SEND_ON_OPTIONS: { value: PracticeDocumentSendOn; label: string }[] = [
  { value: 'confirmation', label: 'When the appointment is booked' },
  { value: 'before', label: 'Before the appointment' },
  { value: 'after', label: 'After the appointment' },
];

/**
 * ONE CHIP: the appointment type, then when it goes out, then whether it is switched off.
 *
 * The appointment type may not have been resolved by the server — a link to a type renamed or
 * removed while this page was open. The id is a poor label but it is not a lie, and it is what
 * lets somebody find the row. A blank there would render a chip saying only "3 weeks before the
 * appointment": a rule with no subject.
 */
export function linkChipLabel(link: PracticeDocumentLinkDto): string {
  const name = link.appointmentTypeName || link.appointmentTypeId;
  const phrase = sendOnPhrase(link.sendOn, link.offsetDays);
  return link.active === false ? `${name} — ${phrase} (paused)` : `${name} — ${phrase}`;
}

/* ── PAUSE IS NOT REMOVE, and the copy is the only thing that says so ───────────────────────
 *
 * §3.2: `active: false` is PAUSE and unlink is a hard DELETE. They are one POST and one DELETE,
 * and from the outside they look identical — a trigger that stops firing. §3.2 names that
 * confusion directly, so every string a user reads about either one says which it is and what
 * survives it.
 */

export const PAUSE_LINK_TOOLTIP =
  'Pause this trigger. It stays configured and stops sending until you resume it.';

export const RESUME_LINK_TOOLTIP = 'Resume this trigger so bookings start sending it again.';

export const REMOVE_LINK_TOOLTIP =
  'Remove this trigger permanently. To stop it temporarily instead, pause it.';

/** The confirmation body for the hard delete, naming the pause it is not. */
export function removeLinkConfirmation(link: PracticeDocumentLinkDto,
  documentName: string): string {
  const name = link.appointmentTypeName || link.appointmentTypeId;
  return `Remove the trigger that sends "${documentName}" for ${name} `
    + `${sendOnPhrase(link.sendOn, link.offsetDays)}? This deletes it — nothing is kept and it `
    + 'cannot be resumed. To stop it temporarily and keep it, pause it instead.';
}

/**
 * THE EMPTY STATE, and the §17 defect it is written not to repeat.
 *
 * "This document is not linked to any appointment type" is a CLAIM ABOUT THE PRACTICE'S DATA, in
 * exactly the sense the tab's document empty state is. The component decides when it may be said;
 * this constant only fixes the words, and they are chosen to state the consequence rather than the
 * absence — a document with no trigger is a document no booking will ever ask for.
 */
export const NO_LINKS_SENTENCE =
  'No appointment type sends this document, so booking one will never ask a patient for it.';

/**
 * THE CEILING THE SERVER ENFORCES: `offsetDays > 730` is `INVALID_OFFSET`.
 *
 * Checked here as well as there, because the weeks control makes it trivially easy to cross — 105
 * is a plausible typo for 10.5 and puts the draft over two years — and a refusal the practice
 * could have been shown before they pressed the button is a round trip spent saying no.
 *
 * 730 days is 104 weeks and a bit, so the weeks control's real limit is 104: a whole number of
 * weeks above that cannot be expressed in days without crossing the ceiling.
 */
export const MAX_OFFSET_DAYS = 730;

/**
 * WHAT IS WRONG WITH THE DRAFT'S OFFSET, in a sentence, or `null` when nothing is.
 *
 * One function rather than three conditions in a template, and it returns the sentence rather than
 * a boolean, because a control that is disabled and does not say why is a control a user reports as
 * broken. Each case is the client's half of a refusal `§24.2` names, and the wording is the same
 * either way round, so a practice who provoked the server's copy and this one would not think they
 * were two different problems.
 */
export function offsetProblem(offsetDays: number): string | null {
  if (!Number.isFinite(offsetDays)) return 'Enter how long before or after the appointment.';
  if (offsetDays < 0) {
    return 'An offset cannot be negative — before and after is chosen above, not by a minus sign.';
  }
  if (!Number.isInteger(offsetDays)) {
    return 'The offset has to be a whole number of days or weeks.';
  }
  if (offsetDays > MAX_OFFSET_DAYS) {
    return `The longest offset is ${MAX_OFFSET_DAYS} days — 104 weeks. Choose a shorter one.`;
  }
  return null;
}

/**
 * WHICH OF THE THREE THINGS THE UPSERT DID — created, reactivated, or paused.
 *
 * The POST answers **200 for all of them**, so the status code says nothing, and the three
 * booleans on the body are the only way to tell (contract correction 3 from wave 4a). It matters
 * because of the inactive-row trap: a practice who paused a trigger, forgot, and adds it again
 * does NOT get a second trigger — they get the paused one back. Told "added", they would go
 * looking for the duplicate; told "resumed", they know exactly what happened.
 */
export function linkOutcomeMessage(
  outcome: { created?: boolean; reactivated?: boolean; paused?: boolean } | null): string {
  if (outcome && outcome.reactivated) {
    return 'That trigger was paused, so it has been resumed rather than added a second time.';
  }
  if (outcome && outcome.paused) return 'Appointment trigger paused.';
  if (outcome && outcome.created) return 'Appointment trigger added.';
  // Neither created nor reactivated: the identical trigger was already there and active.
  return 'That appointment trigger was already set up.';
}

/* ── The refusals, in the words of the practice ──────────────────────────────────────────────
 *
 * The server sends `{ error, message }` and `PracticeDocumentPdfChoice.messageFor` would happily
 * print its `message`. These sentences are preferred over it for the NAMED codes, and only for
 * those, because a controller's message is written for whoever reads a log: it says what the
 * request was missing, not what the person in front of this form should now do. Anything
 * unrecognised falls through to the server's own words — a refusal this build has never heard of
 * is exactly the case where the server knows more than we do.
 *
 * ⚠ **These are the codes the SERVER actually sends, which are not the codes §24.2 drafted.**
 * The contract was a draft written before the controller existed; wave 4a's committed
 * `practiceDocumentController.js` is the authority, and it differs in three ways:
 *
 * 1. `PRACTICE_DOCUMENT_NOT_FOUND`, not the draft's `DOCUMENT_NOT_FOUND`. The draft name is
 *    deliberately NOT kept as an alias — a key nothing can send is a sentence nobody will ever
 *    read, and it would make the next person believe the server has two names for one refusal.
 * 2. Two the draft did not list at all, both on the DELETE: `APPOINTMENT_TYPE_LINK_NOT_FOUND`
 *    and `INVALID_LINK_ID`.
 * 3. **Four more that front ALL THREE calls**, because the GET, the POST and the DELETE each run
 *    `callerGroups` and `loadDocumentInScope` before they do anything of their own:
 *    `AUTHENTICATION_REQUIRED`, `NO_GROUP_MEMBERSHIP`, `INVALID_PRACTICE_DOCUMENT_ID` and
 *    `PRACTICE_DOCUMENT_NOT_FOUND`. These are the ones a practice is most likely to actually
 *    meet — an expired session is more common than a malformed `sendOn` — and they were the ones
 *    missing.
 *
 * ── THE TWO REGISTERS, AND WHY THEY ARE NOT WRITTEN THE SAME WAY ─────────────────────────────
 *
 * `AUTHENTICATION_REQUIRED` and `NO_GROUP_MEMBERSHIP` are SESSION-level facts. Nothing about the
 * trigger the user was editing is wrong, so a sentence about the trigger would send them to fix
 * something that is not broken. They point at signing in again instead. Every other code here is
 * about the thing on the screen, and says what to do to it.
 *
 * `NOT_A_MEMBER` is neither: the controller emits it in exactly one place — the check that the
 * appointment type and the document belong to the SAME practice — so it is a PAIRING refusal, not
 * a membership one, and it can only ever arrive from the POST or the DELETE's link lookup. Worded
 * as "you are not a member" it would be a lie about the user's account.
 */
export const LINK_REFUSAL_MESSAGES: { [code: string]: string } = {
  /* ── Session-level: nothing on this screen is wrong ─────────────────────────────────────── */
  AUTHENTICATION_REQUIRED:
    'Your session has expired, so nothing was read or changed. Sign in again and this page will '
    + 'work as before.',
  NO_GROUP_MEMBERSHIP:
    'Your account is not in any practice at the moment, so there are no appointment triggers to '
    + 'read or change. Sign in again, and ask an administrator to add you if it keeps happening.',

  /* ── The document this row is about ─────────────────────────────────────────────────────── */
  INVALID_PRACTICE_DOCUMENT_ID:
    'This document could not be identified, so nothing was read or changed. Reload this page and '
    + 'try again.',
  PRACTICE_DOCUMENT_NOT_FOUND:
    'That document no longer exists. Someone may have deleted it — reload this page to see the '
    + 'current list.',

  /* ── The trigger being configured ───────────────────────────────────────────────────────── */
  MISSING_FIELDS:
    'Choose an appointment type and when this document should go out — and, for a trigger before '
    + 'or after the appointment, how long. A missing offset is not read as zero.',
  INVALID_SEND_ON:
    'That is not a time a document can be sent. Choose when the appointment is booked, before it, '
    + 'or after it.',
  INVALID_OFFSET:
    `The offset has to be a whole number of days or weeks, from 0 up to ${MAX_OFFSET_DAYS} days. `
    + 'A trigger that sends when the appointment is booked has no offset at all.',
  // A PAIRING refusal, not a membership one — see the header. The two rows are each fine on their
  // own and simply belong to different practices, which is why the sentence names both of them.
  NOT_A_MEMBER:
    'That appointment type belongs to a different practice from this document, so the two cannot '
    + 'be linked. Choose an appointment type from this practice.',
  APPOINTMENT_TYPE_NOT_FOUND:
    'That appointment type no longer exists. Reload this page and choose one that does.',
  // The likeliest real failure of the DELETE: two people on the same settings page.
  APPOINTMENT_TYPE_LINK_NOT_FOUND:
    'That trigger had already been removed, so there was nothing left to delete. The list below '
    + 'has been re-read.',
  INVALID_LINK_ID:
    'That trigger could not be identified, so nothing was removed. Reload this page and try again.',
};

export function linkRefusalMessage(error: any, fallback: string): string {
  const api = PracticeDocumentService.asApiError(error);
  if (api && api.error && LINK_REFUSAL_MESSAGES[api.error]) {
    return LINK_REFUSAL_MESSAGES[api.error];
  }
  return api && api.message ? api.message : fallback;
}

/** The one refusal whose right answer is to go and look again rather than to ask the user to. */
export function linkRefusalCode(error: any): string | null {
  const api = PracticeDocumentService.asApiError(error);
  return api && api.error ? api.error : null;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE ROW'S METADATA, AS SENTENCES RATHER THAN LABELLED VALUES
 *
 * The settings list used to print these as a stack of `<strong>Label:</strong> value` lines, which
 * is not what any other settings tab does and cost seven lines per document — with five documents
 * the section could not be read without scrolling past the thing the practice came to check.
 *
 * A chip has no room for a label beside its value, so the value has to carry its own meaning:
 * "30 days" says nothing on its own, "Link expires after 30 days" is the whole fact. Both of these
 * therefore return a COMPLETE sentence fragment, not a number.
 * ══════════════════════════════════════════════════════════════════════════════════════════ */

/** "1 day" / "30 days" — the plural the rest of these sentences are built from. */
function dayCount(days: number): string {
  return `${days} day${days === 1 ? '' : 's'}`;
}

/** How long a patient's link stays usable, as the chip says it. */
export function expiryLabel(expiryDays: number | null | undefined): string {
  return typeof expiryDays === 'number'
    ? `Link expires after ${dayCount(expiryDays)}`
    : 'Link expiry not set';
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * CATEGORY SUGGESTIONS
 *
 * `category` is free text (§9) and it always will be: a practice groups its documents in its own
 * words, and a fixed list would be us deciding what an SAQ is filed under. What free text costs is
 * "Admission", "admissions" and "Admission " sitting in the same list as three different groups,
 * each with one document under it — which is the same failure as no grouping at all, arrived at
 * one typo per document.
 *
 * So the field stays free and the practice is OFFERED what they have already used. Nothing here
 * constrains what may be saved; both functions below only decide what to put in a panel.
 * ══════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * The categories a practice has actually used, deduplicated CASE-INSENSITIVELY and sorted.
 *
 * The first spelling seen wins, because there is no better answer available: "Admission" and
 * "admission" are one category to the practice, and offering both is precisely how you end up with
 * both. Which of the two survives matters far less than that only one is suggested — a panel that
 * lists a word twice in different cases teaches the practice the field is case-sensitive, which it
 * is not.
 *
 * A blank or whitespace-only category is not a category and is dropped rather than offered as an
 * empty row nobody can read.
 */
export function documentCategories(
  documents: Array<{ category?: string | null }> | null | undefined): string[] {
  if (!documents) return [];

  const firstSpelling = new Map<string, string>();
  documents.forEach((document) => {
    const category = document && document.category ? String(document.category).trim() : '';
    if (!category) return;
    const key = category.toLowerCase();
    if (!firstSpelling.has(key)) firstSpelling.set(key, category);
  });

  return Array.from(firstSpelling.values())
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/**
 * What to offer for what has been typed so far.
 *
 * SUBSTRING, not prefix: a practice reaching for "Medical aid" is as likely to type "aid" as
 * "med", and a prefix match answers only one of those.
 *
 * THE EXACT MATCH IS DROPPED. Once the box holds a category that already exists, the only
 * suggestion left is the thing already in the box — a panel that stays open to tell someone what
 * they have just typed is a panel covering the field they are typing into.
 */
export function matchingCategories(
  categories: string[] | null | undefined, typed: string | null | undefined): string[] {
  const all = categories || [];
  const needle = (typed || '').trim().toLowerCase();
  if (!needle) return all;
  return all.filter((category) => {
    const candidate = category.toLowerCase();
    return candidate.includes(needle) && candidate !== needle;
  });
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * WILL ANYTHING EVER ASK A PATIENT FOR THIS DOCUMENT?
 *
 * The row used to answer this from the three delivery FLAGS alone: no flags meant "nobody will
 * ever be asked for this document", full stop. §24.5 then made appointment triggers the FOURTH
 * delivery route, and that sentence stopped being true — a document with every flag off and a
 * trigger on it goes out every time that appointment type is booked, while the row said in as many
 * words that it never would. Worse, the sentence sat directly above the chips that contradicted it.
 *
 * ── WHY THERE ARE THREE SENTENCES AND NOT TWO ────────────────────────────────────────────────
 *
 * A PAUSED trigger sends nothing, so a row whose only routes are paused triggers is indeed asking
 * nobody for anything — but "ever" would still be wrong, and more importantly the FIX is a
 * different one. "Configure a delivery route" and "resume the trigger you paused" are not the same
 * instruction, and a practice given the first when they needed the second will build a second way
 * of sending a document they had already set up.
 *
 * Nothing here is said when a delivery flag IS set: the chips above have already answered.
 * ══════════════════════════════════════════════════════════════════════════════════════════ */

/** No flags, no triggers at all. The only case where "ever" is honest. */
export const NOTHING_SENDS_IT =
  'No delivery route is configured, so nobody will ever be asked for this document.';

/** No flags, but at least one LIVE trigger — so the document does go out, by one route only. */
export const ONLY_TRIGGERS_SEND_IT =
  'No delivery route is configured, so this document goes out only when one of the appointment '
  + 'triggers below fires.';

/** No flags, triggers exist, and every one of them is paused. Recoverable, and says how. */
export const EVERY_TRIGGER_PAUSED =
  'No delivery route is configured, and every appointment trigger below is paused, so nothing is '
  + 'asking a patient for this document.';

/**
 * Which of the three the row should say, or `null` when the chips have already answered.
 *
 * `active !== false` rather than `active === true`, matching how the chip itself decides whether
 * to draw "(paused)" — a link arriving without the key is LIVE, and reading it the other way round
 * would announce that every trigger is paused on the strength of a serializer that omitted a
 * default.
 *
 * ⚠ The CALLER owns the case this cannot see: a trigger list that is still loading, or that failed
 * to load, supports none of these three sentences. See the component.
 */
export function deliveryReachSentence(
  deliveryRouteCount: number,
  links: Array<{ active?: boolean }> | null | undefined): string | null {
  if (deliveryRouteCount > 0) return null;

  const triggers = links || [];
  if (triggers.length === 0) return NOTHING_SENDS_IT;

  return triggers.some((link) => link.active !== false)
    ? ONLY_TRIGGERS_SEND_IT
    : EVERY_TRIGGER_PAUSED;
}
